import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import LoginSheet from "./LoginSheet";
import {
  LogOut,
  User as UserIcon,
  Crown,
  Sparkles,
  Settings,
  Loader2,
  UserCircle,
  X,
} from "lucide-react";

export default function AccountButton({ openPaywall }) {
  const { user, usage, loading, signIn, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  async function openCustomerPortal() {
    setPortalBusy(true);
    try {
      // T49.E — iOS users MUST be routed to Apple's subscription
      // management (App Store Connect rules); only web hits the
      // Stripe customer portal. `manageSubscription` branches on
      // platform internally.
      const { manageSubscription } = await import("../lib/manageSubscription");
      await manageSubscription({
        returnUrl: window.location.origin + "/",
      });
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Couldn't open subscription portal");
    } finally {
      setPortalBusy(false);
    }
  }

  if (loading) return null;

  if (!user) {
    return (
      <>
        <button
          type="button"
          onClick={() => setLoginOpen(true)}
          data-testid="signin-btn"
          aria-label="Sign in"
          title="Sign in"
          className="w-10 h-10 rounded-full flex items-center justify-center bg-white hover:bg-bone/70 transition-colors text-navy shadow-soft"
        >
          <UserCircle size={28} strokeWidth={1.75} />
        </button>
        <LoginSheet open={loginOpen} onClose={() => setLoginOpen(false)} />
      </>
    );
  }

  const plan = user.plan;
  const safeName = (user.name || "").trim();
  const safeEmail = (user.email || "").trim();
  // Pull initials from the name first, then fall back to the email's local part.
  let initials = "";
  if (safeName) {
    initials = safeName
      .split(/\s+/)
      .map((s) => s[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  } else if (safeEmail) {
    initials = safeEmail.split("@")[0].slice(0, 2).toUpperCase();
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="account-btn"
        className="flex items-center gap-1.5 bg-white rounded-full shadow-soft px-2 py-1 hover:bg-bone/70 transition-colors"
        aria-label="Account"
      >
        <div className="w-7 h-7 rounded-full bg-bone text-navy flex items-center justify-center text-[11px] font-jakarta font-extrabold overflow-hidden">
          {initials ? initials : <UserIcon size={13} strokeWidth={2.5} />}
        </div>
        {plan === "pro" && (
          <span
            className="w-3 h-3 rounded-full bg-forest mr-0.5"
            aria-label="Pro member"
            title="Pro member"
            data-testid="plan-dot-pro"
          />
        )}
        {plan === "trial" && (
          <span
            className="w-3 h-3 rounded-full bg-yellow-400 mr-0.5"
            aria-label="Trial member"
            title="Trial member"
            data-testid="plan-dot-trial"
          />
        )}
      </button>

      {open && (
        <div
          data-testid="account-menu-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
          className="fixed inset-0 z-50 bg-navy/40 flex items-start justify-center p-3 pt-6"
        >
          <div
            className="w-full max-w-md bg-white rounded-3xl shadow-float p-5 space-y-4"
            data-testid="account-menu"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 rounded-full bg-forest text-white flex items-center justify-center text-base font-jakarta font-extrabold flex-shrink-0">
                  {initials ? initials : <UserIcon size={20} strokeWidth={2.5} />}
                </div>
                <div className="min-w-0">
                  <div className="font-jakarta font-extrabold text-navy text-lg truncate">
                    {safeName || "Traveler"}
                  </div>
                  <div className="font-jakarta text-navy-soft text-xs truncate">
                    {safeEmail}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="account-menu-close"
                className="w-9 h-9 rounded-full bg-bone/70 flex items-center justify-center flex-shrink-0"
              >
                <X size={18} strokeWidth={2.5} color="#0E2C54" />
              </button>
            </div>

            {/* Plan + usage card */}
            <div className="bg-bone/40 rounded-2xl p-4 space-y-2">
              <Row k="Plan" v={planLabel(plan, user)} accent />
              {usage?.credit_balance > 0 && (
                <Row k="Credits" v={usage.credit_balance} />
              )}
              {plan !== "pro" && usage?.used && (
                <>
                  <Row
                    k="Today: text"
                    v={`${usage.used.translate_text}/${usage.limits?.translate_text ?? "∞"}`}
                  />
                  <Row
                    k="Today: voice"
                    v={`${usage.used.translate_voice}/${usage.limits?.translate_voice ?? "∞"}`}
                  />
                  <Row
                    k="Today: receipt"
                    v={`${usage.used.receipt}/${usage.limits?.receipt ?? "∞"}`}
                  />
                  <Row
                    k="Today: menu"
                    v={`${usage.used.menu}/${usage.limits?.menu ?? "∞"}`}
                  />
                </>
              )}
            </div>

            {/* Actions */}
            {plan !== "pro" && (
              <button
                onClick={() => {
                  setOpen(false);
                  openPaywall();
                }}
                data-testid="open-paywall-btn"
                className="w-full flex items-center justify-center gap-2 bg-orange text-white rounded-full py-3.5 font-jakarta font-extrabold text-sm active:scale-[0.98] transition-transform shadow-soft"
              >
                <Crown size={16} strokeWidth={2.5} /> Go Pro
              </button>
            )}

            {plan === "pro" && (
              <button
                onClick={openCustomerPortal}
                disabled={portalBusy}
                data-testid="manage-subscription-btn"
                className="w-full flex items-center justify-center gap-2 bg-navy text-white rounded-full py-3.5 font-jakarta font-extrabold text-sm disabled:opacity-60 active:scale-[0.98] transition-transform shadow-soft"
              >
                {portalBusy ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Settings size={16} strokeWidth={2.5} />
                )}
                Manage subscription
              </button>
            )}

            <button
              onClick={signOut}
              data-testid="signout-btn"
              className="w-full flex items-center justify-center gap-2 bg-white border border-navy/10 rounded-full py-3 font-jakarta font-bold text-navy text-sm active:scale-[0.98] transition-transform"
            >
              <LogOut size={15} strokeWidth={2.5} /> Sign out
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function planLabel(plan, user) {
  if (plan === "pro") {
    const d = user.pro_until ? new Date(user.pro_until).toLocaleDateString() : "";
    return `Pro · until ${d}`;
  }
  if (plan === "trial") {
    const d = user.trial_ends_at
      ? new Date(user.trial_ends_at).toLocaleDateString()
      : "";
    return `Trial · ends ${d}`;
  }
  return "Free";
}

function Row({ k, v, accent = false }) {
  return (
    <div className="flex items-baseline justify-between font-jakarta text-sm">
      <span className="text-navy-soft font-medium">{k}</span>
      <span
        className={`${
          accent ? "text-navy font-extrabold" : "text-navy font-bold"
        }`}
      >
        {v}
      </span>
    </div>
  );
}
